// System administration → AI → Image generation.
//
// The third independent choice in this console, and deliberately the
// "Document search" design again: whose account pays for the pictures the
// Assistant puts into the content it writes (a blog post's infographic is drawn
// without any model; a photo or illustration needs an image model).
//
//   ''          nothing chosen — the application owner's Settings → AI image
//               profile keeps serving, as before this existed
//   own_key     this organization's own OpenAI / Google key
//   platform    AI Credit: the instance administrator's image key, charged to
//               this organization's credits
//
// Controlled by RuntimeAiPanel so it rides the panel's one Save; its Test is
// its own (a free model lookup — no picture is generated to check a key).

import { useState } from 'react'
import { CheckCircle2, Loader2, XCircle, Zap } from 'lucide-react'
import {
  testRuntimeImage,
  type RuntimeAiConfigView, type RuntimeImageTestResult, type RuntimeImageUpdate,
} from '@/api/runtimeAi'

const PROVIDER_LABEL: Record<string, string> = { openai: 'OpenAI', google: 'Google (Imagen)' }

export const IMAGE_SOURCE_LABEL: Record<string, string> = {
  '': 'Not chosen — use the application owner’s image profile',
  own_key: 'Our own provider key',
  platform: 'AI Credit (this instance’s image key)',
}

/** Why this image choice cannot generate, or '' — mirrors the endpoint. */
export function imageConfigError(value: RuntimeImageUpdate, view: RuntimeAiConfigView | null): string {
  if (value.source === 'platform') {
    return view?.image_platform_available === false
      ? 'AI Credit cannot generate images on this instance: no platform image key is configured. '
        + 'Use your own OpenAI or Google key, or ask an instance administrator to set one.'
      : ''
  }
  if (value.source === 'own_key') {
    if (!value.provider) return 'Choose an image provider — only OpenAI and Google generate images.'
    if (!(value.api_key || '').trim() && !view?.image?.credential_configured) {
      return 'Add the image provider’s API key — without it the Assistant cannot generate pictures.'
    }
  }
  return ''
}

export function RuntimeImageSection({ ownerId, projectId, view, value, onChange, disabled, inputStyle, buttonStyle }: {
  ownerId: string
  projectId: string
  view: RuntimeAiConfigView | null
  value: RuntimeImageUpdate
  onChange: (next: RuntimeImageUpdate) => void
  disabled?: boolean
  inputStyle: React.CSSProperties
  buttonStyle: React.CSSProperties
}) {
  const [testing, setTesting] = useState(false)
  const [test, setTest] = useState<RuntimeImageTestResult | null>(null)
  const providers = view?.image_providers || ['openai', 'google']
  const platformAvailable = view?.image_platform_available !== false
  const defaults = view?.image_default_models || {}
  const models = (value.provider && view?.image_models?.[value.provider]) || []
  const problem = imageConfigError(value, view)
  const set = (patch: Partial<RuntimeImageUpdate>) => { onChange({ ...value, ...patch }); setTest(null) }

  const runTest = async () => {
    setTesting(true); setTest(null)
    try {
      setTest(await testRuntimeImage(ownerId, projectId, {
        source: value.source, provider: value.provider, model: value.model,
        api_key: (value.api_key || '').trim() || undefined,
      }))
    } catch (e) {
      setTest({ ok: false, error: e instanceof Error ? e.message : 'The test failed.' })
    } finally { setTesting(false) }
  }

  const hint: React.CSSProperties = { fontSize: 13, color: '#64748b' }
  return (
    <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, display: 'grid', gap: 10 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Image generation</div>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
          When the Assistant writes content — a post, an email, a product page — it can add
          pictures. Infographics are drawn from your data with no model at all; photos and
          illustrations need an image model. Only OpenAI and Google generate images, so this
          is a separate provider from the assistant above.
        </p>
      </div>

      <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
        Images from
        <select style={inputStyle} value={value.source} disabled={disabled}
          onChange={(e) => {
            const next = e.target.value as RuntimeImageUpdate['source']
            set({ source: next, model: '',
                  provider: next === 'own_key' ? (value.provider || providers[0] || 'openai') : value.provider })
          }}>
          <option value="">{IMAGE_SOURCE_LABEL['']}</option>
          <option value="own_key">{IMAGE_SOURCE_LABEL.own_key}</option>
          <option value="platform" disabled={!platformAvailable}>
            {platformAvailable ? IMAGE_SOURCE_LABEL.platform
              : `${IMAGE_SOURCE_LABEL.platform} — unavailable on this instance`}
          </option>
        </select>
        {!value.source && (
          <span style={hint}>
            Pictures use the application owner’s own image profile, on their account, when
            they have one; otherwise only infographics are added.
          </span>
        )}
      </label>

      {value.source === 'own_key' && (
        <>
          <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
            Provider
            <select style={inputStyle} value={value.provider || ''} disabled={disabled}
              onChange={(e) => set({ provider: e.target.value, model: '' })}>
              <option value="">Select a provider…</option>
              {providers.map((id) => <option key={id} value={id}>{PROVIDER_LABEL[id] || id}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
            Model <span style={hint}>(optional)</span>
            <input style={inputStyle} list="runtime-image-models" value={value.model || ''} disabled={disabled}
              placeholder={defaults[value.provider || ''] || ''}
              onChange={(e) => set({ model: e.target.value })} />
            <datalist id="runtime-image-models">
              {models.map((m) => <option key={m} value={m} />)}
            </datalist>
            <span style={hint}>
              Leave blank for <b>{defaults[value.provider || ''] || 'the provider default'}</b>.
            </span>
          </label>
          <label style={{ display: 'grid', gap: 5, fontSize: 14 }}>
            API key
            <input style={inputStyle} type="password" autoComplete="new-password" disabled={disabled}
              value={value.api_key || ''}
              onChange={(e) => set({ api_key: e.target.value })}
              placeholder={view?.image?.credential_configured
                ? '•••••••• stored — type to replace' : 'Paste the image provider key'} />
            <span style={hint}>
              Stored encrypted and never shown again. Leave blank to keep the stored key.
            </span>
          </label>
        </>
      )}

      {value.source === 'platform' && platformAvailable && (
          <span style={hint}>
            Pictures are generated with this instance’s image key
            {view?.image_platform?.provider
              ? ` (${PROVIDER_LABEL[view.image_platform.provider] || view.image_platform.provider}`
                + `${view.image_platform.model ? ` · ${view.image_platform.model}` : ''})`
              : ''}
            , and each one is charged to <b>your organization’s</b> AI credits.
          </span>
      )}

      {problem && value.source && (
        <div style={{ fontSize: 13.5, background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px' }}>
          {problem}
        </div>
      )}

      {view?.image?.configured && view.image.effective_model && (
        <span style={hint}>Currently generating with <b>{view.image.effective_model}</b>.</span>
      )}

      {test && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, borderRadius: 8,
          padding: '9px 11px', border: `1px solid ${test.ok ? '#bbf7d0' : '#fecaca'}`,
          background: test.ok ? '#f0fdf4' : '#fef2f2', color: test.ok ? '#166534' : '#b91c1c',
        }}>
          {test.ok ? <CheckCircle2 size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            : <XCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
          <span style={{ wordBreak: 'break-word' }}>
            {test.ok ? (test.message || 'The key can reach the image model.') : (test.error || 'The test failed.')}
          </span>
        </div>
      )}

      {!!value.source && (
        <div>
          <button type="button" style={buttonStyle} onClick={runTest}
            disabled={testing || disabled || !!problem}
            title={problem || 'Checks the key can reach the model — no picture is generated'}>
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Test image key
          </button>
        </div>
      )}
    </div>
  )
}
